import { useCallback, useState } from 'react'
import axios from 'axios'
import { httpClientService, vesselStoreService } from '@services'
import { downloadCertificatePdf } from '@utils'
import { useLanguage } from '@i18n'
import type { Certificate, Vessel } from '@app-types/index'

type DownloadCertificateOptions = {
  onDownloadedVessel?: (vessel: Vessel) => void
  onComplete?: () => void
  requireUtn?: boolean
  vesselName?: string
}

const invalidUtnStatusCodes = new Set([400, 404])

const isInvalidUtnError = (error: unknown) =>
  axios.isAxiosError(error) && error.response?.status !== undefined && invalidUtnStatusCodes.has(error.response.status)

export const useCertificateDownload = () => {
  const { t } = useLanguage()
  const [isDownloading, setIsDownloading] = useState(false)
  const [downloadError, setDownloadError] = useState('')

  const clearDownloadError = useCallback(() => {
    setDownloadError('')
  }, [])

  const downloadCertificate = async (
    certificate: Certificate | null,
    utnNumber: string,
    options: DownloadCertificateOptions = {}
  ) => {
    if (!certificate) {
      return false
    }

    const trimmedUtnNumber = utnNumber.trim()

    if (options.requireUtn && !trimmedUtnNumber) {
      setDownloadError(t('certificateDownload.missingUtn'))
      return false
    }

    if (certificate.pdf) {
      downloadCertificatePdf(certificate, options.vesselName)
      options.onComplete?.()
      return true
    }

    if (!trimmedUtnNumber || !certificate.uuid) {
      setDownloadError(t('certificateDownload.missingUtn'))
      return false
    }

    try {
      setIsDownloading(true)
      setDownloadError('')

      const response = await httpClientService.getPDFStatus(trimmedUtnNumber, true, certificate.uuid)
      const responseVessel = response.data
      const responseCertificate = responseVessel.certificates?.find((item) => item.uuid === certificate.uuid)

      if (!responseCertificate) {
        setDownloadError(t('certificateDownload.incorrectUtn'))
        return false
      }

      if (!responseCertificate.pdf) {
        setDownloadError(t('certificateDownload.incorrectUtn'))
        return false
      }

      vesselStoreService.setVessel(responseVessel)
      options.onDownloadedVessel?.(responseVessel)
      downloadCertificatePdf(
        responseCertificate,
        responseVessel.vesselName?.trim() ? responseVessel.vesselName : options.vesselName
      )
      options.onComplete?.()
      return true
    } catch (error) {
      setDownloadError(t(isInvalidUtnError(error) ? 'certificateDownload.incorrectUtn' : 'certificateDownload.genericError'))
      return false
    } finally {
      setIsDownloading(false)
    }
  }

  return {
    clearDownloadError,
    downloadCertificate,
    downloadError,
    isDownloading,
  }
}
